if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mediaItems = [];
        this.currentIndex = 0;
        this.isZoomed = false;
        this.zoomScale = 1;
        this.touch = null;
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onStageClick = this.onStageClick.bind(this);
        this.onStageMove = this.onStageMove.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
        this.bindControls();
      }

      bindControls() {
        this.addEventListener('click', (event) => {
          const prev = event.target.closest('[data-modal-prev]');
          if (prev) {
            event.stopPropagation();
            this.navigate(-1);
            return;
          }
          const next = event.target.closest('[data-modal-next]');
          if (next) {
            event.stopPropagation();
            this.navigate(1);
            return;
          }
          const thumb = event.target.closest('[data-modal-thumb]');
          if (thumb) {
            event.stopPropagation();
            const id = thumb.getAttribute('data-media-id');
            const index = this.mediaItems.findIndex((el) => el.getAttribute('data-media-id') === id);
            if (index >= 0) this.goTo(index);
            return;
          }
          const closer = event.target.closest('[data-modal-close]');
          if (closer && closer.id?.startsWith('ModalClose-') === false) {
            event.stopPropagation();
            this.hide();
          }
        });
      }

      get isFocused() {
        return this.classList.contains('product-media-modal--focused');
      }

      get isDesktop() {
        return window.matchMedia('(min-width: 750px)').matches;
      }

      cacheMedia() {
        const stage = this.querySelector('[data-modal-stage]') || this.querySelector('.product-media-modal__content');
        if (!stage) {
          this.mediaItems = [];
          return;
        }
        this.mediaItems = Array.from(stage.children).filter((el) => {
          if (!el.hasAttribute('data-media-id')) return false;
          if (el.classList.contains('product__media-item--variant')) return false;
          return true;
        });
        const total = this.querySelector('[data-modal-total]');
        if (total) total.textContent = String(Math.max(this.mediaItems.length, 1));
      }

      show(opener) {
        super.show(opener);
        this.cacheMedia();
        this.showActiveMedia();
        document.addEventListener('keydown', this.onKeyDown);
        if (this.isFocused && !this.isDesktop) {
          document.body.classList.add('product-lightbox-open');
        }
        const stage = this.querySelector('[data-modal-stage]');
        if (stage) {
          stage.addEventListener('click', this.onStageClick);
          stage.addEventListener('mousemove', this.onStageMove);
          stage.addEventListener('touchstart', this.onTouchStart, { passive: false });
          stage.addEventListener('touchmove', this.onTouchMove, { passive: false });
          stage.addEventListener('touchend', this.onTouchEnd, { passive: false });
          stage.addEventListener('touchcancel', this.onTouchEnd, { passive: false });
        }
      }

      hide() {
        document.body.classList.remove('product-lightbox-open');
        document.removeEventListener('keydown', this.onKeyDown);
        const stage = this.querySelector('[data-modal-stage]');
        if (stage) {
          stage.removeEventListener('click', this.onStageClick);
          stage.removeEventListener('mousemove', this.onStageMove);
          stage.removeEventListener('touchstart', this.onTouchStart);
          stage.removeEventListener('touchmove', this.onTouchMove);
          stage.removeEventListener('touchend', this.onTouchEnd);
          stage.removeEventListener('touchcancel', this.onTouchEnd);
        }
        this.resetZoom();
        super.hide();
      }

      onKeyDown(event) {
        if (!this.hasAttribute('open') || !this.isFocused) return;
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.navigate(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.navigate(1);
        }
      }

      navigate(delta) {
        if (!this.mediaItems.length) return;
        const next = (this.currentIndex + delta + this.mediaItems.length) % this.mediaItems.length;
        this.goTo(next);
      }

      goTo(index) {
        if (!this.mediaItems.length) return;
        this.currentIndex = (index + this.mediaItems.length) % this.mediaItems.length;
        this.resetZoom();
        this.setActiveMedia(this.mediaItems[this.currentIndex]);
      }

      showActiveMedia() {
        this.cacheMedia();
        const openerId = this.openedBy?.getAttribute('data-media-id');
        let active =
          this.mediaItems.find((el) => el.getAttribute('data-media-id') === openerId) || this.mediaItems[0];

        if (!active) {
          active = this.querySelector(`[data-media-id="${openerId}"]`) || this.querySelector('[data-media-id]');
        }

        if (!active) return;

        this.currentIndex = Math.max(
          0,
          this.mediaItems.findIndex((el) => el === active)
        );
        this.setActiveMedia(active);
      }

      setActiveMedia(activeMedia) {
        this.querySelectorAll('[data-media-id].active').forEach((el) => el.classList.remove('active'));
        activeMedia.classList.add('active');

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (this.isFocused) {
          activeMedia.scrollIntoView({ block: 'nearest', inline: 'nearest' });
        } else {
          activeMedia.scrollIntoView();
          const container = this.querySelector('[role="document"]');
          if (container && activeMedia.width) {
            container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
          }
        }

        this.syncChrome(activeMedia);

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      syncChrome(activeMedia) {
        const id = activeMedia.getAttribute('data-media-id');
        const indexEl = this.querySelector('[data-modal-index]');
        if (indexEl) indexEl.textContent = String(this.currentIndex + 1);

        this.querySelectorAll('[data-modal-thumb]').forEach((thumb) => {
          const selected = thumb.getAttribute('data-media-id') === id;
          thumb.setAttribute('aria-selected', selected ? 'true' : 'false');
          thumb.classList.toggle('is-active', selected);
          if (selected && this.isFocused) {
            thumb.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
          }
        });

        const multi = this.mediaItems.length > 1;
        this.querySelectorAll('[data-modal-prev], [data-modal-next]').forEach((btn) => {
          // Arrows only on desktop; mobile uses swipe
          btn.hidden = !multi || !this.isFocused || !this.isDesktop;
        });
        const chrome = this.querySelector('.product-media-modal__chrome');
        if (chrome) chrome.hidden = !this.isFocused;
      }

      activeImage() {
        return this.querySelector('[data-modal-stage] img.active, .product-media-modal__content img.active');
      }

      onStageClick(event) {
        if (!this.isFocused || !this.isDesktop) return;
        if (event.target.closest('deferred-media, product-model, button, a')) return;
        const img = event.target.closest('img');
        if (!img || !img.classList.contains('active')) return;
        event.stopPropagation();
        this.toggleZoom(img, event);
      }

      onStageMove(event) {
        if (!this.isZoomed || !this.isFocused || !this.isDesktop) return;
        const img = this.activeImage();
        if (!img) return;
        const rect = img.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width) * 100;
        const y = ((event.clientY - rect.top) / rect.height) * 100;
        img.style.transformOrigin = `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`;
      }

      toggleZoom(img, event) {
        if (this.isZoomed || this.zoomScale > 1.05) {
          this.resetZoom();
          return;
        }
        this.setZoom(2, event?.clientX, event?.clientY, img);
      }

      setZoom(scale, clientX, clientY, img = this.activeImage()) {
        if (!img) return;
        this.zoomScale = scale;
        this.isZoomed = scale > 1.05;
        this.classList.toggle('product-media-modal--zoomed', this.isZoomed);
        img.classList.toggle('is-zoomed', this.isZoomed);
        if (clientX != null && clientY != null) {
          const rect = img.getBoundingClientRect();
          const x = ((clientX - rect.left) / rect.width) * 100;
          const y = ((clientY - rect.top) / rect.height) * 100;
          img.style.transformOrigin = `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`;
        }
        img.style.transform = this.isZoomed ? `scale(${scale})` : '';
      }

      resetZoom() {
        this.isZoomed = false;
        this.zoomScale = 1;
        this.classList.remove('product-media-modal--zoomed');
        this.querySelectorAll('img.is-zoomed, [data-modal-stage] img, .product-media-modal__content img').forEach(
          (img) => {
            img.classList.remove('is-zoomed');
            img.style.transformOrigin = '';
            img.style.transform = '';
          }
        );
        this.touch = null;
      }

      touchDistance(a, b) {
        const dx = a.clientX - b.clientX;
        const dy = a.clientY - b.clientY;
        return Math.hypot(dx, dy);
      }

      onTouchStart(event) {
        if (!this.isFocused) return;
        if (event.touches.length === 2) {
          event.preventDefault();
          this.touch = {
            mode: 'pinch',
            startDist: this.touchDistance(event.touches[0], event.touches[1]),
            startScale: this.zoomScale || 1,
          };
          return;
        }
        if (event.touches.length === 1) {
          this.touch = {
            mode: this.zoomScale > 1.05 ? 'pan' : 'swipe',
            startX: event.touches[0].clientX,
            startY: event.touches[0].clientY,
            lastX: event.touches[0].clientX,
            lastY: event.touches[0].clientY,
            originX: 50,
            originY: 50,
          };
          const img = this.activeImage();
          if (img && this.touch.mode === 'pan') {
            const rect = img.getBoundingClientRect();
            this.touch.originX = ((event.touches[0].clientX - rect.left) / rect.width) * 100;
            this.touch.originY = ((event.touches[0].clientY - rect.top) / rect.height) * 100;
          }
        }
      }

      onTouchMove(event) {
        if (!this.isFocused || !this.touch) return;

        if (this.touch.mode === 'pinch' && event.touches.length === 2) {
          event.preventDefault();
          const dist = this.touchDistance(event.touches[0], event.touches[1]);
          const scale = Math.min(4, Math.max(1, this.touch.startScale * (dist / this.touch.startDist)));
          const midX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
          const midY = (event.touches[0].clientY + event.touches[1].clientY) / 2;
          this.setZoom(scale, midX, midY);
          return;
        }

        if (this.touch.mode === 'pan' && event.touches.length === 1) {
          event.preventDefault();
          const img = this.activeImage();
          if (!img) return;
          const dx = event.touches[0].clientX - this.touch.lastX;
          const dy = event.touches[0].clientY - this.touch.lastY;
          this.touch.lastX = event.touches[0].clientX;
          this.touch.lastY = event.touches[0].clientY;
          this.touch.originX = Math.max(0, Math.min(100, this.touch.originX - (dx / img.clientWidth) * 50));
          this.touch.originY = Math.max(0, Math.min(100, this.touch.originY - (dy / img.clientHeight) * 50));
          img.style.transformOrigin = `${this.touch.originX}% ${this.touch.originY}%`;
          return;
        }

        if (this.touch.mode === 'swipe' && event.touches.length === 1) {
          const dx = event.touches[0].clientX - this.touch.startX;
          const dy = event.touches[0].clientY - this.touch.startY;
          // Prevent page scroll when clearly swiping horizontally in lightbox
          if (Math.abs(dx) > 12 && Math.abs(dx) > Math.abs(dy)) {
            event.preventDefault();
          }
        }
      }

      onTouchEnd(event) {
        if (!this.isFocused || !this.touch) return;

        if (this.touch.mode === 'pinch') {
          if (event.touches.length >= 2) return;
          if (this.zoomScale < 1.15) this.resetZoom();
          this.touch = event.touches.length === 1
            ? {
                mode: this.zoomScale > 1.05 ? 'pan' : 'swipe',
                startX: event.touches[0].clientX,
                startY: event.touches[0].clientY,
                lastX: event.touches[0].clientX,
                lastY: event.touches[0].clientY,
                originX: 50,
                originY: 50,
              }
            : null;
          return;
        }

        if (event.touches.length > 0) return;

        if (this.touch.mode === 'swipe') {
          const t = event.changedTouches[0];
          const dx = t.clientX - this.touch.startX;
          const dy = t.clientY - this.touch.startY;
          const absX = Math.abs(dx);
          const absY = Math.abs(dy);

          if (absY > 90 && absY > absX * 1.15 && dy > 0) {
            this.hide();
          } else if (absX > 48 && absX > absY) {
            this.navigate(dx < 0 ? 1 : -1);
          }
        }

        this.touch = null;
      }
    }
  );
}
